import { Button } from "reactstrap";
import pluralize from "pluralize";
import { useMemo } from "react";

import { ReusableViewReferenceFields } from "../../../types/PageInterfaces";
import { Field, Module } from "../../../types/TenantInterfaces";
import SectionRenderer from "../../Records/SectionRenderer";
import ManageRecordTags from "../../Records/ManageRecordTags";
import useModule from "../../../hooks/useModule";

interface Props {
  record: any;
  fieldSchema: Field;
  module: Module;
  onView?: any;
  fields: any;
  maxHeight: number;
  useTitle: boolean;
  readOnly: boolean;
  moduleAPIName: string;
  parentRecord: any;
  reusableViewReferenceFields: ReusableViewReferenceFields;
}

const SummaryCardView = ({
  record,
  fieldSchema,
  onView,
  fields = [],
  maxHeight = 400,
  useTitle = true, // Whether or not title should be displayed
  readOnly = true,
  moduleAPIName = "", // Used for empty state
  parentRecord = null,
  module,
  reusableViewReferenceFields,
}: Props) => {
  const { getModule } = useModule(record?._ModuleAPIName || moduleAPIName);
  const fieldType = fieldSchema?.FieldType;

  const fieldsWithColSpan = fields?.map((f: any) => ({
    Field: f,
    ColSpan: 1,
  }));
  const section = {
    Fields: fieldsWithColSpan,
    Title: "",
    Type: fieldType,
  };

  const message = useMemo(() => {
    let message;
    let name;

    const isSubitem = ["Subitem", "MultiSubitem"].includes(fieldType);

    if (isSubitem) {
      name = fieldSchema?.Name ?? "";
    } else {
      name = (module?.Name || getModule(moduleAPIName)?.Name) ?? "";
    }

    switch (fieldType) {
      case "SingleReference":
        message =
          "No " + pluralize.singular(name || "record") + " is selected.";
        break;
      case "MultiReference":
        message =
          "No " +
          pluralize(name) +
          " are selected. Click edit to choose " +
          pluralize(name) +
          ".";
        break;
      case "Subitem":
        message =
          "No " + pluralize.singular(name || "record") + " has been added.";
        break;
      case "MultiSubitem":
        message = "No " + pluralize(name) + " have been added.";
        break;
      default:
        message = "Unknown field type.";
    }

    return (
      <div className="text-dark bg-lighter py-4 px-5 rounded-bottom font-weight-500">
        {message}
      </div>
    );
  }, [module?.Name, moduleAPIName, fieldType, record]);

  if (!record) {
    return message;
  }

  return (
    <>
      {useTitle && (
        <div
          className={`d-flex justify-content-between align-items-center rounded-bottom bg-dropdown-selected--hover cursor-pointer`}
          onClick={onView ? () => onView() : undefined}
        >
          <div className="px-3 py-2 ">
            <div
              style={{ fontSize: 12 }}
              className="text-muted font-weight-500"
            >
              {module?.Name && pluralize.singular(module?.Name)}
            </div>
            <h3 className="mb-0">{record?._RecordName}</h3>
            {/* @ts-ignore */}
            <ManageRecordTags
              moduleAPIName={record?._ModuleAPIName || moduleAPIName}
              recordId={record?.id}
              recordTagIds={record?.Tags || []}
              mode="r"
            />
          </div>
          {onView && (
            <Button className="mr-3 btn-icon-only" size="sm" outline>
              <i className="mdi mdi-chevron-right"></i>
            </Button>
          )}
        </div>
      )}
      {!useTitle && (
        <div
          className="p-3"
          style={{ maxHeight: maxHeight, overflowY: "auto" }}
        >
          <SectionRenderer
            module={module}
            record={record}
            parentRecord={parentRecord}
            section={section}
            marginBottom={false}
            useCard={false}
            readOnly={readOnly}
            isCard={true}
            reusableViewReferenceFields={reusableViewReferenceFields}
          />
        </div>
      )}
    </>
  );
};

export default SummaryCardView;
